import React, { useState, useRef, useEffect, useCallback } from 'react';
import { StyleSheet, View, Text, TextInput, Platform, TouchableOpacity, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InputField } from './InputField';
import { MicButton } from './MicButton';
import { SendButton } from './SendButton';
import { useTutor } from '../contexts/TutorContext';
import { useChatContext } from '../contexts/ChatContext';
import { useSettings } from '../contexts/SettingsContext';
import { processText, processAudio } from '../api';
import { audioRecordingService } from '../services/audio/audioRecordingService';
import SimpleAudioVisualizer from './SimpleAudioVisualizer';
import type { Message, SupportedLanguage } from '../types';

interface UserInfo {
  name: string;
  email: string;
}

interface ChatInputProps {
  openMenuWindow: () => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  userInfo: UserInfo;
  selectedLanguage: SupportedLanguage;
  getTranslation: (key: string) => string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  openMenuWindow,
  messages,
  setMessages,
  userInfo,
  selectedLanguage,
  getTranslation
}) => {
  const { selectedTutor } = useTutor();
  const { addMessage, isWaitingForAI, setIsWaitingForAI, sessionId } = useChatContext();
  const { interfaceLanguage } = useSettings();
  
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isLongPress, setIsLongPress] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showShortAudioMessage, setShowShortAudioMessage] = useState(false);
  const [hasAudioPermission, setHasAudioPermission] = useState<boolean | null>(true);
  const [lastMessageTime, setLastMessageTime] = useState(Date.now());
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const recordingTimer = useRef<NodeJS.Timeout | null>(null);
  const recordingInterval = useRef<NodeJS.Timeout | null>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<TextInput>(null);
  const buttonScale = useRef(new Animated.Value(1)).current;
  const [rippleScale] = useState(new Animated.Value(0));
  const [rippleOpacity] = useState(new Animated.Value(0));

  useEffect(() => {
    const checkPermission = async () => {
      if (Platform.OS === 'web') {
        try {
          const permission = await navigator.mediaDevices.getUserMedia({ audio: true });
          setHasAudioPermission(true);
          if (permission) {
            permission.getTracks().forEach(track => track.stop());
          }
        } catch (err) {
          console.error('Error getting audio permission:', err);
          setHasAudioPermission(false);
        }
      } else {
        setHasAudioPermission(true);
      }
    };
    checkPermission();
  }, []);

  useEffect(() => {
    return () => {
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
      }
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || !selectedTutor) return;

    const messageText = inputText.trim();
    setInputText('');
    setIsWaitingForAI(true);
    setLastMessageTime(Date.now());

    try {
      addMessage({
        text: messageText,
        type: 'user'
      });

      const response = await processText({
        text: messageText,
        language: selectedTutor.language_code,
        tutorId: selectedTutor.id,
        sessionId: sessionId
      });
      
      if (response.status === 'success' && response.response) {
        addMessage({
          text: response.response,
          type: 'bot',
          isFromNewResponse: true
        });
      }
    } catch (error) {
      console.error('Error processing message:', error);
    } finally {
      setIsWaitingForAI(false);
    }
  }, [inputText, selectedTutor, addMessage, sessionId]);

  const handleAudioInput = async (audioFile: File | string) => {
    try {
      setIsProcessing(true);
      setIsWaitingForAI(true);

      if (selectedTutor) {
        const response = await processAudio(
          audioFile,
          {
            language: selectedTutor.language_code,
            tutorId: selectedTutor.id,
            sessionId: sessionId,
            platform: Platform.OS,
          }
        );

        if (response) {
          if (response.transcript) {
            addMessage({
              text: response.transcript,
              type: 'user',
              audioUrl: response.audioUrl
            });
          }

          if (response.response) {
            addMessage({
              text: response.response,
              type: 'bot',
              isFromNewResponse: true
            });
          }
        }
      }
    } catch (error) {
      console.error('Error processing audio:', error);
    } finally {
      setIsProcessing(false);
      setIsWaitingForAI(false);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      if (isRecording) return;
      
      setIsRecording(true);
      setRecordingDuration(0);
      await audioRecordingService.startRecording();
      
      recordingInterval.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }
    }
  }, [isRecording]);

  const stopRecording = useCallback(async () => {
    if (!isRecording) return;

    try {
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }

      setIsProcessing(true);
      const result = await audioRecordingService.stopRecording();
      
      if (!result.success) {
        console.warn('Error stopping recording:', result.error);
        return;
      }

      if (result.audioBlob) {
        const audioFile = new File([result.audioBlob], 'recording.webm', {
          type: result.audioBlob.type
        });
        await handleAudioInput(audioFile);
      } else if (result.audioUrl) {
        await handleAudioInput(result.audioUrl);
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
    } finally {
      setIsRecording(false);
      setIsProcessing(false);
      setRecordingDuration(0);
    }
  }, [isRecording, handleAudioInput]);

  useEffect(() => {
    return () => {
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
      }
      audioRecordingService.reset();
    };
  }, []);

  return (
    <View style={styles.container}>
      {showShortAudioMessage && (
        <Text style={styles.shortAudioMessage}>
          {getTranslation('shortAudio')}
        </Text>
      )}
      
      <View style={styles.inputContainer}>
        {!isRecording ? (
          <InputField
            ref={inputRef}
            value={inputText}
            onChangeText={setInputText}
            onSubmitEditing={handleSend}
            placeholder={selectedTutor ? 
              getTranslation('typeMessage') : 
              getTranslation('selectTutor')}
            style={[
              styles.input,
              !selectedTutor && styles.disabledInput
            ]}
            editable={!!selectedTutor}
          />
        ) : (
          <View style={styles.visualizerContainer}>
            <View style={styles.visualizerContent}>
              <View style={styles.visualizerWrapper}>
                {audioStream && (
                  <SimpleAudioVisualizer
                    stream={audioStream}
                    barCount={20}
                    barColor="#007AFF"
                    barStyle="pill"
                    barWidth={3}
                    minBarHeight={2}
                    maxBarHeight={20}
                    spacing={2}
                  />
                )}
              </View>
              <View style={styles.durationWrapper}>
                <Text style={styles.recordingDuration}>
                  {recordingDuration}s
                </Text>
              </View>
            </View>
          </View>
        )}
        
        {inputText.length > 0 ? (
          <TouchableOpacity
            style={[
              styles.actionButton,
              isWaitingForAI && styles.actionButtonActive,
              !selectedTutor && styles.actionButtonDisabled,
            ]}
            onPress={handleSend}
            disabled={!inputText.trim() || isWaitingForAI || !selectedTutor}
          >
            <Ionicons name="send" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        ) : (
          <MicButton
            isRecording={isRecording}
            isDisabled={!hasAudioPermission || isProcessing}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            recordingDuration={recordingDuration}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 800 : '100%',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#E6E6E6',
    gap: 12,
  },
  inputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  input: {
    flex: 1,
    backgroundColor: '#F7F7F7',
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 10,
    color: '#333333',
    fontSize: 14,
    minHeight: 44,
    lineHeight: 20,
  },
  actionButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#4A90E2',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  actionButtonActive: {
    backgroundColor: '#357ABD',
    transform: [{ scale: 0.95 }],
  },
  actionButtonDisabled: {
    backgroundColor: '#B8D4F5',
    opacity: 0.7,
  },
  disabledInput: {
    backgroundColor: '#F5F5F5',
    color: '#999999',
  },
  shortAudioMessage: {
    fontSize: 11,
    color: '#666666',
    marginBottom: 4,
    fontWeight: '500',
  },
  visualizerContainer: {
    flex: 1,
    height: 44,
    backgroundColor: '#F7F7F7',
    borderRadius: 24,
    overflow: 'hidden',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  visualizerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  },
  visualizerWrapper: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
  },
  durationWrapper: {
    marginLeft: 12,
    minWidth: 40,
    alignItems: 'flex-end',
  },
  recordingDuration: {
    fontSize: 14,
    color: '#FF3B30',
    fontWeight: '600',
  },
});